import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const api = axios.create({
  baseURL: `${API_URL}/api`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Response interceptor to handle errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Only redirect if we had a token (prevents redirect loops during initial load)
      const currentToken = localStorage.getItem('token');
      if (currentToken) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        // Use replace to avoid back-button loops
        if (window.location.pathname !== '/login' && window.location.pathname !== '/register') {
          window.location.replace('/login');
        }
      }
    }
    return Promise.reject(error);
  }
);

// Dashboard
export const getDashboardStats = () => api.get('/dashboard/stats');
export const getRecentPackages = (limit = 10) => api.get(`/dashboard/recent-packages?limit=${limit}`);
export const getPackagesByStatus = () => api.get('/dashboard/packages-by-status');

// Customers
export const getCustomers = (params) => api.get('/customers', { params });
export const getCustomer = (id) => api.get(`/customers/${id}`);
export const createCustomer = (data) => api.post('/customers', data);
export const updateCustomer = (id, data) => api.put(`/customers/${id}`, data);
export const deleteCustomer = (id) => api.delete(`/customers/${id}`);
export const importCustomers = (file) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post('/customers/import', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
};

// Packages
export const getPackages = (params) => api.get('/packages', { params });
export const getPackage = (id) => api.get(`/packages/${id}`);
export const createPackage = (data) => api.post('/packages', data);
export const updatePackage = (id, data) => api.put(`/packages/${id}`, data);
export const updatePackageStatus = (id, status) => api.put(`/packages/${id}/status?status=${status}`);
export const deletePackage = (id) => api.delete(`/packages/${id}`);
export const sendPackageInvoice = (id) => api.post(`/packages/${id}/send-invoice`);
export const trackPackage = (trackingNumber) => api.get(`/packages/track/${trackingNumber}`);
export const scanPackage = (trackingNumber) => api.post(`/packages/scan?tracking_number=${trackingNumber}`);
export const batchUpdateStatus = (data) => api.post('/packages/batch-status', data);
export const importPackagesCsv = (data) => api.post('/packages/import-csv', data);

// Invoices
export const getInvoices = (params) => api.get('/invoices', { params });
export const getInvoice = (id) => api.get(`/invoices/${id}`);
export const createInvoice = (data) => api.post('/invoices', data);
export const updateInvoiceStatus = (id, status) => api.put(`/invoices/${id}/status?status=${status}`);
export const downloadInvoicePdf = (id) => api.get(`/invoices/${id}/pdf`, { responseType: 'blob' });
export const getPublicInvoice = (invoiceNumber) => api.get(`/invoices/public/${invoiceNumber}`);
export const getPublicInvoicePdfUrl = (invoiceNumber) => `${API_URL}/api/invoices/public/${invoiceNumber}/pdf`;

// Payments
export const getPayments = (params) => api.get('/payments', { params });
export const createPaymentCheckout = (invoiceId, paymentMethod = 'stripe') => api.post(`/payments/create-checkout?invoice_id=${invoiceId}&payment_method=${paymentMethod}`);
export const capturePaypalInvoicePayment = (orderId) => api.post(`/payments/paypal-capture/${orderId}`);
export const getPaymentStatus = (sessionId) => api.get(`/payments/status/${sessionId}`);

// Notifications
export const getNotifications = (params) => api.get('/notifications', { params });
export const markNotificationRead = (id) => api.put(`/notifications/${id}/read`);
export const markAllNotificationsRead = () => api.put('/notifications/read-all');
export const getUnreadCount = () => api.get('/notifications/unread-count');

// Settings
export const getPricing = () => api.get('/settings/pricing');
export const getWhatsAppSettings = () => api.get('/settings/whatsapp');
export const testWhatsApp = (phone) => api.post(`/settings/whatsapp/test?phone=${encodeURIComponent(phone)}`);
export const getEmailSettings = () => api.get('/settings/email');
export const testEmail = (email) => api.post(`/settings/email/test?email=${encodeURIComponent(email)}`);

// Universal Tracking
export const universalTrack = (trackingNumber) => api.post('/universal-track', { tracking_number: trackingNumber });
export const getCarriers = () => api.get('/carriers');

// Company Plan
export const getCompanyPlan = () => api.get('/companies/my-plan');
export const upgradePlan = () => api.post('/companies/upgrade');

// Customer Service Add-on
export const getCustomerServiceAddon = () => api.get('/companies/addon/customer-service');
export const subscribeCustomerService = () => api.post('/companies/addon/customer-service/subscribe');
export const cancelCustomerService = () => api.post('/companies/addon/customer-service/cancel');

// Billing
export const getBillingOverview = () => api.get('/companies/billing');
export const getBillingHistory = () => api.get('/companies/billing/history');
export const cancelSubscription = () => api.post('/companies/billing/cancel');
export const reactivateSubscription = () => api.post('/companies/billing/reactivate');

// Auto-login after payment
export const autoLoginAfterPayment = (sessionId) => api.post('/companies/auto-login', { session_id: sessionId });

// Branding
export const getBranding = () => api.get('/settings/branding');
export const updateBranding = (data) => api.put('/settings/branding', data);
export const uploadLogo = (data) => api.post('/settings/branding/logo', data);

// Delivery Proof
export const uploadDeliveryProof = (packageId, data) => api.post(`/packages/${packageId}/delivery-proof`, data);
export const getDeliveryProof = (packageId) => api.get(`/packages/${packageId}/delivery-proof`);

// Custom Domain
export const getDomain = () => api.get('/companies/domain');
export const setDomain = (data) => api.post('/companies/domain', data);
export const verifyDomain = () => api.post('/companies/domain/verify');
export const removeDomain = () => api.delete('/companies/domain');

// Public Branding (no auth required)
export const getPublicBranding = (companyId) => api.get(`/companies/${companyId}/public-branding`);

// Tracking Email Subscriptions
export const subscribeTracking = (data) => api.post('/track/subscribe', data);
export const unsubscribeTracking = (email, trackingNumber) => api.delete(`/track/unsubscribe?email=${email}&tracking_number=${trackingNumber}`);

export default api;
